import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  LogOut, RefreshCw, Search, Eye, Trash2, 
  Clock, CheckCircle, XCircle, Phone, Mail,
  Car, FileText, ChevronDown, ChevronUp, Image,
  Star, Plus, Download, MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_tire-quote-portal/artifacts/nn6a64ea_IMG_1623.png";

const STATUS_CONFIG = {
  pending: { label: "En attente", color: "bg-amber-500 text-black" },
  contacted: { label: "Contacté", color: "bg-blue-500 text-white" },
  quoted: { label: "Devis envoyé", color: "bg-purple-500 text-white" },
  completed: { label: "Terminé", color: "bg-emerald-500 text-white" },
  cancelled: { label: "Annulé", color: "bg-red-500 text-white" }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialDialogOpen, setTestimonialDialogOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({
    client_name: "",
    vehicle: "",
    rating: 5,
    comment: "",
    is_visible: true
  });
  
  // PDF Quote state
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfItems, setPdfItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [pdfNotes, setPdfNotes] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const token = localStorage.getItem("pp_admin_token");
  const username = localStorage.getItem("pp_admin_username");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    verifyToken();
    fetchData();
  }, [token, navigate]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      localStorage.removeItem("pp_admin_token");
      localStorage.removeItem("pp_admin_username");
      navigate("/admin");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotesRes, statsRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/testimonials?visible_only=false`)
      ]);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (quoteId, newStatus, adminNotes = "") => {
    try {
      await axios.put(
        `${API}/quotes/${quoteId}/status`,
        { status: newStatus, admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Statut mis à jour");
      fetchData();
      setDetailsOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) return;
    
    try {
      await axios.delete(`${API}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Demande supprimée");
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("pp_admin_token");
    localStorage.removeItem("pp_admin_username");
    navigate("/admin");
  };

  // Testimonial handlers
  const handleSaveTestimonial = async () => {
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, testimonialForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Témoignage mis à jour");
      } else {
        await axios.post(`${API}/testimonials`, testimonialForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Témoignage ajouté");
      }
      setTestimonialDialogOpen(false);
      setEditingTestimonial(null);
      setTestimonialForm({ client_name: "", vehicle: "", rating: 5, comment: "", is_visible: true });
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Témoignage supprimé");
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditTestimonial = (testimonial) => {
    setEditingTestimonial(testimonial);
    setTestimonialForm({
      client_name: testimonial.client_name,
      vehicle: testimonial.vehicle || "",
      rating: testimonial.rating,
      comment: testimonial.comment,
      is_visible: testimonial.is_visible
    });
    setTestimonialDialogOpen(true);
  };

  // PDF Generation
  const openPdfDialog = (quote) => {
    setSelectedQuote(quote);
    setPdfItems([{ description: "", quantity: 1, unit_price: 0 }]);
    setPdfNotes("");
    setPdfDialogOpen(true);
  };

  const addPdfItem = () => {
    setPdfItems([...pdfItems, { description: "", quantity: 1, unit_price: 0 }]);
  };

  const removePdfItem = (index) => {
    setPdfItems(pdfItems.filter((_, i) => i !== index));
  };

  const updatePdfItem = (index, field, value) => {
    const newItems = [...pdfItems];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setPdfItems(newItems);
  };

  const generatePdf = async () => {
    if (!selectedQuote) return;
    
    const validItems = pdfItems.filter(item => item.description && item.unit_price > 0);
    if (validItems.length === 0) {
      toast.error("Ajoutez au moins un article avec un prix");
      return;
    }

    setGeneratingPdf(true);
    try {
      const response = await axios.post(
        `${API}/quotes/${selectedQuote.id}/generate-pdf`,
        {
          quote_id: selectedQuote.id,
          items: validItems,
          notes: pdfNotes,
          validity_days: 30
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devis_pp_performance_${selectedQuote.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Devis PDF généré !");
      setPdfDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.customer?.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.customer?.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.customer?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.customer?.phone?.includes(searchQuery);
    
    const matchesStatus = statusFilter === "all" || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServicesText = (services) => {
    if (!services) return "Aucun";
    const active = [];
    if (services.oil_change) active.push("Vidange");
    if (services.oil_filter) active.push("Filtre huile");
    if (services.air_filter) active.push("Filtre air");
    if (services.cabin_filter) active.push("Filtre habitacle");
    if (services.brake_discs) active.push("Disques");
    if (services.brake_pads) active.push("Plaquettes");
    if (services.diagnostic) active.push("Diagnostic");
    if (services.other_services) active.push("Autre");
    return active.length > 0 ? active.join(", ") : "Aucun";
  };

  const getTireText = (tires) => {
    if (!tires || (!tires.width && !tires.aspect_ratio && !tires.diameter)) return "Non demandé";
    return `${tires.width || '?'}/${tires.aspect_ratio || '?'} R${tires.diameter || '?'} - ${tires.tire_type || 'Type ?'} - ${tires.brand_preference || 'Marque ?'} x${tires.quantity || 4}`;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="dashboard-logo-link">
              <img src={LOGO_URL} alt="PP Performance" className="h-10" />
            </Link>
            <div className="hidden md:block h-8 w-px bg-zinc-800" />
            <span className="hidden md:block text-zinc-400 uppercase tracking-wider text-sm">Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-zinc-400 text-sm hidden md:block">
              Connecté: <span className="text-white">{username}</span>
            </span>
            <Button 
              variant="ghost" 
              onClick={fetchData}
              className="text-zinc-400 hover:text-white"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-zinc-900/30 border border-zinc-800 p-4" data-testid="stat-total">
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Total</p>
              <p className="text-3xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-zinc-900/30 border border-zinc-800 p-4" data-testid="stat-pending">
              <p className="text-amber-500 text-xs uppercase tracking-wider mb-1">En attente</p>
              <p className="text-3xl font-bold">{stats.pending}</p>
            </div>
            <div className="bg-zinc-900/30 border border-zinc-800 p-4" data-testid="stat-contacted">
              <p className="text-blue-500 text-xs uppercase tracking-wider mb-1">Contactés</p>
              <p className="text-3xl font-bold">{stats.contacted}</p>
            </div>
            <div className="bg-zinc-900/30 border border-zinc-800 p-4" data-testid="stat-quoted">
              <p className="text-purple-500 text-xs uppercase tracking-wider mb-1">Devis envoyés</p>
              <p className="text-3xl font-bold">{stats.quoted}</p>
            </div>
            <div className="bg-zinc-900/30 border border-zinc-800 p-4" data-testid="stat-completed">
              <p className="text-emerald-500 text-xs uppercase tracking-wider mb-1">Terminés</p>
              <p className="text-3xl font-bold">{stats.completed}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="mb-6 bg-zinc-900/50 border border-zinc-800">
            <TabsTrigger value="quotes" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" />
              Demandes
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <MessageSquare className="w-4 h-4 mr-2" />
              Témoignages ({testimonials.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quotes">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par nom, email, téléphone..."
              className="bg-zinc-900/50 border-zinc-800 pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48 bg-zinc-900/50 border-zinc-800" data-testid="status-filter">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="contacted">Contacté</SelectItem>
              <SelectItem value="quoted">Devis envoyé</SelectItem>
              <SelectItem value="completed">Terminé</SelectItem>
              <SelectItem value="cancelled">Annulé</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-zinc-900/20 border border-zinc-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800 bg-zinc-900/50">
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-medium">Client</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-medium hidden md:table-cell">Véhicule</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-medium hidden lg:table-cell">Pneus</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-medium hidden lg:table-cell">Services</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-medium">Statut</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-zinc-500 font-medium hidden md:table-cell">Date</th>
                  <th className="text-right p-4 text-xs uppercase tracking-wider text-zinc-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-zinc-500">
                      Chargement...
                    </td>
                  </tr>
                ) : filteredQuotes.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-zinc-500">
                      Aucune demande trouvée
                    </td>
                  </tr>
                ) : (
                  filteredQuotes.map((quote) => (
                    <>
                      <tr 
                        key={quote.id} 
                        className="border-b border-zinc-800/50 table-row-hover cursor-pointer"
                        onClick={() => setExpandedRow(expandedRow === quote.id ? null : quote.id)}
                        data-testid={`quote-row-${quote.id}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="md:hidden">
                              {expandedRow === quote.id ? 
                                <ChevronUp className="w-4 h-4 text-zinc-500" /> : 
                                <ChevronDown className="w-4 h-4 text-zinc-500" />
                              }
                            </div>
                            <div>
                              <p className="font-medium">{quote.customer?.first_name} {quote.customer?.last_name}</p>
                              <p className="text-zinc-500 text-sm">{quote.customer?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <p className="text-sm">
                            {quote.vehicle?.make} {quote.vehicle?.model}
                          </p>
                          <p className="text-zinc-500 text-xs">{quote.vehicle?.year}</p>
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <p className="text-sm text-zinc-400">{getTireText(quote.tires)}</p>
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <p className="text-sm text-zinc-400 max-w-[200px] truncate">{getServicesText(quote.services)}</p>
                        </td>
                        <td className="p-4">
                          <Badge className={`${STATUS_CONFIG[quote.status]?.color} uppercase text-xs tracking-wider`}>
                            {STATUS_CONFIG[quote.status]?.label}
                          </Badge>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <p className="text-sm text-zinc-400">{formatDate(quote.created_at)}</p>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedQuote(quote);
                                setDetailsOpen(true);
                              }}
                              className="text-zinc-400 hover:text-white"
                              data-testid={`view-quote-${quote.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(quote.id);
                              }}
                              className="text-zinc-400 hover:text-red-500"
                              data-testid={`delete-quote-${quote.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {/* Mobile expanded row */}
                      {expandedRow === quote.id && (
                        <tr className="md:hidden bg-zinc-900/30">
                          <td colSpan={7} className="p-4 border-b border-zinc-800/50">
                            <div className="space-y-3 text-sm">
                              <div>
                                <span className="text-zinc-500">Véhicule: </span>
                                <span>{quote.vehicle?.make} {quote.vehicle?.model} {quote.vehicle?.year}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500">Pneus: </span>
                                <span className="text-zinc-400">{getTireText(quote.tires)}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500">Services: </span>
                                <span className="text-zinc-400">{getServicesText(quote.services)}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500">Date: </span>
                                <span className="text-zinc-400">{formatDate(quote.created_at)}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-xl font-bold">Gérer les témoignages</h2>
              <Button 
                onClick={() => {
                  setEditingTestimonial(null);
                  setTestimonialForm({ client_name: "", vehicle: "", rating: 5, comment: "", is_visible: true });
                  setTestimonialDialogOpen(true);
                }}
                className="bg-white text-black hover:bg-zinc-200"
                data-testid="add-testimonial-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un témoignage
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.length === 0 ? (
                <div className="col-span-full text-center py-12 text-zinc-500">
                  Aucun témoignage. Ajoutez-en un pour qu'il apparaisse sur votre site.
                </div>
              ) : (
                testimonials.map((testimonial) => (
                  <div 
                    key={testimonial.id}
                    className={`bg-zinc-900/30 border p-6 ${testimonial.is_visible ? 'border-zinc-800' : 'border-red-900/50 opacity-60'}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-4 h-4 ${i < testimonial.rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-700'}`} 
                          />
                        ))}
                      </div>
                      {!testimonial.is_visible && (
                        <Badge variant="outline" className="text-red-500 border-red-500">Masqué</Badge>
                      )}
                    </div>
                    <p className="text-zinc-300 text-sm mb-4 line-clamp-3">"{testimonial.comment}"</p>
                    <p className="font-bold text-sm">{testimonial.client_name}</p>
                    {testimonial.vehicle && (
                      <p className="text-zinc-500 text-xs">{testimonial.vehicle}</p>
                    )}
                    <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-800">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openEditTestimonial(testimonial)}
                        className="flex-1"
                      >
                        Modifier
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteTestimonial(testimonial.id)}
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Quote Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight">
              Détails de la demande
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Demande #{selectedQuote?.id?.slice(0, 8)}
            </DialogDescription>
          </DialogHeader>

          {selectedQuote && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <div className="bg-zinc-800/30 border border-zinc-700 p-4">
                <h4 className="font-bold uppercase tracking-wider text-sm mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Client
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-zinc-500">Nom:</span>
                    <p>{selectedQuote.customer?.first_name} {selectedQuote.customer?.last_name}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Téléphone:</span>
                    <p>{selectedQuote.customer?.phone}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Email:</span>
                    <p>{selectedQuote.customer?.email}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Adresse:</span>
                    <p>{selectedQuote.customer?.address || "-"}, {selectedQuote.customer?.city || ""} {selectedQuote.customer?.postal_code || ""}</p>
                  </div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="bg-zinc-800/30 border border-zinc-700 p-4">
                <h4 className="font-bold uppercase tracking-wider text-sm mb-3 flex items-center gap-2">
                  <Car className="w-4 h-4" /> Véhicule
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-zinc-500">Marque/Modèle:</span>
                    <p>{selectedQuote.vehicle?.make} {selectedQuote.vehicle?.model}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Année:</span>
                    <p>{selectedQuote.vehicle?.year || "-"}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">N° Châssis (VIN):</span>
                    <p>{selectedQuote.vehicle?.license_plate || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Tires */}
              {selectedQuote.tires && (
                <div className="bg-zinc-800/30 border border-zinc-700 p-4">
                  <h4 className="font-bold uppercase tracking-wider text-sm mb-3">Pneus demandés</h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-zinc-500">Dimensions:</span>
                      <p>{selectedQuote.tires.width}/{selectedQuote.tires.aspect_ratio} R{selectedQuote.tires.diameter}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Type:</span>
                      <p>{selectedQuote.tires.tire_type || "-"}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Marque:</span>
                      <p>{selectedQuote.tires.brand_preference || "-"}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Quantité:</span>
                      <p>{selectedQuote.tires.quantity}</p>
                    </div>
                  </div>
                  {selectedQuote.tires.additional_notes && (
                    <div className="mt-3">
                      <span className="text-zinc-500 text-sm">Notes:</span>
                      <p className="text-sm">{selectedQuote.tires.additional_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Services */}
              <div className="bg-zinc-800/30 border border-zinc-700 p-4">
                <h4 className="font-bold uppercase tracking-wider text-sm mb-3">Services demandés</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedQuote.services?.oil_change && <Badge variant="secondary">Vidange</Badge>}
                  {selectedQuote.services?.oil_filter && <Badge variant="secondary">Filtre huile</Badge>}
                  {selectedQuote.services?.air_filter && <Badge variant="secondary">Filtre air</Badge>}
                  {selectedQuote.services?.cabin_filter && <Badge variant="secondary">Filtre habitacle</Badge>}
                  {selectedQuote.services?.brake_discs && <Badge variant="secondary">Disques</Badge>}
                  {selectedQuote.services?.brake_pads && <Badge variant="secondary">Plaquettes</Badge>}
                  {selectedQuote.services?.diagnostic && <Badge variant="secondary">Diagnostic</Badge>}
                </div>
                {selectedQuote.services?.other_services && (
                  <div className="mt-3">
                    <span className="text-zinc-500 text-sm">Autres:</span>
                    <p className="text-sm">{selectedQuote.services.other_services}</p>
                  </div>
                )}
              </div>

              {/* Additional Info */}
              {(selectedQuote.preferred_date || selectedQuote.message) && (
                <div className="bg-zinc-800/30 border border-zinc-700 p-4">
                  <h4 className="font-bold uppercase tracking-wider text-sm mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Informations
                  </h4>
                  {selectedQuote.preferred_date && (
                    <div className="mb-2">
                      <span className="text-zinc-500 text-sm">Date souhaitée:</span>
                      <p className="text-sm">{selectedQuote.preferred_date}</p>
                    </div>
                  )}
                  {selectedQuote.message && (
                    <div>
                      <span className="text-zinc-500 text-sm">Message:</span>
                      <p className="text-sm">{selectedQuote.message}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Attachments / Photos */}
              {selectedQuote.attachments && selectedQuote.attachments.length > 0 && (
                <div className="bg-zinc-800/30 border border-zinc-700 p-4">
                  <h4 className="font-bold uppercase tracking-wider text-sm mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Documents joints ({selectedQuote.attachments.length})
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedQuote.attachments.map((attachment, index) => (
                      <a 
                        key={index}
                        href={attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block border border-zinc-700 hover:border-zinc-500 transition-colors overflow-hidden"
                      >
                        <img 
                          src={attachment} 
                          alt={`Document ${index + 1}`}
                          className="w-full h-32 object-cover hover:scale-105 transition-transform"
                        />
                      </a>
                    ))}
                  </div>
                  <p className="text-zinc-500 text-xs mt-2">Cliquez sur une image pour l'agrandir</p>
                </div>
              )}

              {/* Status Update */}
              <div className="border-t border-zinc-800 pt-4">
                <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">
                  Mettre à jour le statut
                </Label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                    <Button
                      key={status}
                      variant={selectedQuote.status === status ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleStatusUpdate(selectedQuote.id, status)}
                      className={selectedQuote.status === status ? config.color : ""}
                      data-testid={`status-btn-${status}`}
                    >
                      {config.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">
                  Notes administrateur
                </Label>
                <Textarea 
                  defaultValue={selectedQuote.admin_notes || ""}
                  placeholder="Ajouter des notes..."
                  className="bg-zinc-800/50 border-zinc-700"
                  onBlur={(e) => {
                    if (e.target.value !== selectedQuote.admin_notes) {
                      handleStatusUpdate(selectedQuote.id, selectedQuote.status, e.target.value);
                    }
                  }}
                  data-testid="admin-notes-textarea"
                />
              </div>

              {/* Generate PDF Button */}
              <div className="border-t border-zinc-800 pt-4">
                <Button 
                  onClick={() => {
                    setDetailsOpen(false);
                    openPdfDialog(selectedQuote);
                  }}
                  className="w-full bg-white text-black hover:bg-zinc-200"
                  data-testid="generate-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Générer un devis PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Testimonial Dialog */}
      <Dialog open={testimonialDialogOpen} onOpenChange={setTestimonialDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight">
              {editingTestimonial ? "Modifier le témoignage" : "Ajouter un témoignage"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Nom du client *</Label>
              <Input 
                value={testimonialForm.client_name}
                onChange={(e) => setTestimonialForm({...testimonialForm, client_name: e.target.value})}
                className="bg-zinc-800/50 border-zinc-700"
                placeholder="Jean Dupont"
              />
            </div>
            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Véhicule (optionnel)</Label>
              <Input 
                value={testimonialForm.vehicle}
                onChange={(e) => setTestimonialForm({...testimonialForm, vehicle: e.target.value})}
                className="bg-zinc-800/50 border-zinc-700"
                placeholder="Peugeot 308"
              />
            </div>
            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Note</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setTestimonialForm({...testimonialForm, rating})}
                    className="p-1"
                  >
                    <Star 
                      className={`w-8 h-8 ${rating <= testimonialForm.rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-700'}`} 
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Commentaire *</Label>
              <Textarea 
                value={testimonialForm.comment}
                onChange={(e) => setTestimonialForm({...testimonialForm, comment: e.target.value})}
                className="bg-zinc-800/50 border-zinc-700"
                placeholder="Service excellent, je recommande..."
                rows={4}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_visible"
                checked={testimonialForm.is_visible}
                onChange={(e) => setTestimonialForm({...testimonialForm, is_visible: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="is_visible" className="cursor-pointer">Visible sur le site</Label>
            </div>
            <Button 
              onClick={handleSaveTestimonial}
              className="w-full bg-white text-black hover:bg-zinc-200"
              disabled={!testimonialForm.client_name || !testimonialForm.comment}
            >
              {editingTestimonial ? "Mettre à jour" : "Ajouter"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Generation Dialog */}
      <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight">
              Générer un devis PDF
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Client: {selectedQuote?.customer?.first_name} {selectedQuote?.customer?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Articles du devis</Label>
              
              {pdfItems.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input 
                    value={item.description}
                    onChange={(e) => updatePdfItem(index, 'description', e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 flex-1"
                    placeholder="Description (ex: Pneu Michelin 205/55 R16)"
                  />
                  <Input 
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updatePdfItem(index, 'quantity', e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 w-20"
                    placeholder="Qté"
                    min="1"
                  />
                  <Input 
                    type="number"
                    value={item.unit_price}
                    onChange={(e) => updatePdfItem(index, 'unit_price', e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 w-28"
                    placeholder="Prix €"
                    min="0"
                    step="0.01"
                  />
                  {pdfItems.length > 1 && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removePdfItem(index)}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={addPdfItem}
                className="mt-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une ligne
              </Button>
            </div>

            {/* Total preview */}
            <div className="bg-zinc-800/30 border border-zinc-700 p-4">
              <div className="flex justify-between text-sm">
                <span>Total HT:</span>
                <span>{pdfItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-sm text-zinc-400">
                <span>TVA (17%):</span>
                <span>{(pdfItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) * 0.17).toFixed(2)} €</span>
              </div>
              <div className="flex justify-between font-bold mt-2 pt-2 border-t border-zinc-700">
                <span>Total TTC:</span>
                <span>{(pdfItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) * 1.17).toFixed(2)} €</span>
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Notes (optionnel)</Label>
              <Textarea 
                value={pdfNotes}
                onChange={(e) => setPdfNotes(e.target.value)}
                className="bg-zinc-800/50 border-zinc-700"
                placeholder="Conditions particulières, délais, etc."
                rows={3}
              />
            </div>

            <Button 
              onClick={generatePdf}
              className="w-full bg-white text-black hover:bg-zinc-200"
              disabled={generatingPdf}
            >
              <Download className="w-4 h-4 mr-2" />
              {generatingPdf ? "Génération en cours..." : "Télécharger le PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
